import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, UserPlus, Wrench, Plus, X as XIcon } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import CustomerSelect from "@/components/CustomerSelect";

export default function OrderNew() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);

  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [ncName, setNcName] = useState("");
  const [ncPhone, setNcPhone] = useState("");
  const [ncEmail, setNcEmail] = useState("");

  const [form, setForm] = useState({
    customer_id: "",
    category_id: "",
    subcategory_id: "",
    brand: "",
    model: "",
    serial: "",
    accessories: "",
    reception_branch_id: "",
    technician_id: "",
    problem_reported: "",
    observations: "",
    // Reparación Express
    is_express: false,
    express_surcharge: 0,
    express_auto_approve: false,
  });

  // Multi-equipo: equipos adicionales dentro de la MISMA orden
  const [extraDevices, setExtraDevices] = useState([]); // [{category_id, subcategory_id, brand, model, serial, accessories, problem_reported, technician_id}]
  const [extraSubs, setExtraSubs] = useState({}); // { deviceIndex: [subcategory,...] }
  const [extraModels, setExtraModels] = useState({}); // { deviceIndex: [model,...] } — modelos filtrados por subcategoría+marca de ese equipo

  useEffect(() => {
    Promise.all([
      api.get("/customers"),
      api.get("/branches"),
      api.get("/categories"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/brands").catch(() => ({ data: [] })),
    ]).then(([c, b, cat, u, br]) => {
      setCustomers(c.data);
      const activeBranches = b.data.filter((x) => x.active);
      setBranches(activeBranches);
      setCategories(cat.data.filter((x) => x.active));
      setTechnicians((u.data || []).filter((x) => x.role === "technician" && x.active));
      setBrands((br.data || []).filter((x) => x.active));
      if (activeBranches[0])
        setForm((f) => ({ ...f, reception_branch_id: activeBranches[0].id }));
    });
  }, []);

  useEffect(() => {
    if (!form.category_id) {
      setSubcategories([]);
      return;
    }
    api
      .get(`/subcategories?category_id=${form.category_id}`)
      .then((r) => setSubcategories(r.data.filter((s) => s.active)))
      .catch(() => setSubcategories([]));
  }, [form.category_id]);

  // Selected subcategory (may include default_brand_id)
  const selectedSub = useMemo(
    () => subcategories.find((s) => s.id === form.subcategory_id) || null,
    [subcategories, form.subcategory_id],
  );

  // Auto-select brand when the subcategory has a default brand.
  useEffect(() => {
    if (!selectedSub) return;
    if (!selectedSub.default_brand_id) return;
    const b = brands.find((x) => x.id === selectedSub.default_brand_id);
    if (b && form.brand !== b.name) {
      setForm((f) => ({ ...f, brand: b.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subcategory_id, brands]);

  // Load models when subcategory (and optionally brand) changes.
  useEffect(() => {
    if (!form.subcategory_id) {
      setModels([]);
      return;
    }
    // Match brand name → id for filter (when brand is one of the seeded ones)
    const brandObj = brands.find((b) => b.name.toLowerCase() === (form.brand || "").toLowerCase());
    const brand_id = brandObj?.id;
    const params = new URLSearchParams({ subcategory_id: form.subcategory_id });
    if (brand_id) params.append("brand_id", brand_id);
    api
      .get(`/models?${params.toString()}`)
      .then((r) => setModels((r.data || []).filter((m) => m.active !== false)))
      .catch(() => setModels([]));
  }, [form.subcategory_id, form.brand, brands]);

  // When category changes → clear subcategory, brand, model.
  function handleCategoryChange(newCatId) {
    setForm((f) => ({
      ...f,
      category_id: newCatId,
      subcategory_id: "",
      brand: "",
      model: "",
    }));
  }

  // When subcategory changes → clear brand & model (auto-select brand runs in useEffect).
  function handleSubcategoryChange(newSubId) {
    setForm((f) => ({
      ...f,
      subcategory_id: newSubId,
      brand: "",
      model: "",
    }));
  }

  // When brand changes → clear model if it does not exist for the new brand.
  function handleBrandChange(newBrand) {
    setForm((f) => {
      // If newBrand is empty (Otro/manual selected) or different from previous → clear model.
      if (!newBrand || newBrand !== f.brand) {
        return { ...f, brand: newBrand, model: "" };
      }
      return { ...f, brand: newBrand };
    });
  }

  function upd(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function createInlineCustomer() {
    if (!ncName || !ncPhone) {
      toast.error("Nombre y teléfono son requeridos");
      return;
    }
    try {
      const { data } = await api.post("/customers", {
        name: ncName,
        phone: ncPhone,
        email: ncEmail,
      });
      toast.success("Cliente creado");
      setCustomers((c) => [data, ...c]);
      upd("customer_id", data.id);
      setShowNewCustomer(false);
      setNcName("");
      setNcPhone("");
      setNcEmail("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_id || !form.category_id || !form.subcategory_id || !form.reception_branch_id || !form.problem_reported) {
      toast.error("Completa los campos requeridos");
      return;
    }
    if (serialRequired && !form.serial.trim()) {
      toast.error(`Serial / IMEI obligatorio para ${selectedCategoryName.toLowerCase()}`);
      return;
    }
    // Validar equipos adicionales
    for (let i = 0; i < extraDevices.length; i++) {
      const d = extraDevices[i];
      if (!d.category_id || !d.subcategory_id || !d.problem_reported?.trim()) {
        toast.error(`Equipo adicional #${i + 2}: completa categoría, subcategoría y problema`);
        return;
      }
      const catName = categories.find((c) => c.id === d.category_id)?.name || "";
      if (SERIAL_REQUIRED_CATEGORY_NAMES.includes(catName) && !(d.serial || "").trim()) {
        toast.error(`Equipo adicional #${i + 2}: serial obligatorio para ${catName.toLowerCase()}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Technicians auto-assign themselves so the order appears in their list
      if (user.role === "technician") {
        payload.technician_id = user.id;
      }
      if (!payload.technician_id) delete payload.technician_id;
      if (extraDevices.length > 0) {
        payload.extra_devices = extraDevices.map((d) => {
          const clean = { ...d };
          if (!clean.technician_id) delete clean.technician_id;
          return clean;
        });
      }
      const { data } = await api.post("/orders", payload);
      const total = 1 + extraDevices.length;
      toast.success(`Orden ${data.order_number} creada${total > 1 ? ` con ${total} equipos` : ""}`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  // Multi-equipo helpers
  function addExtraDevice() {
    setExtraDevices((prev) => [
      ...prev,
      { category_id: "", subcategory_id: "", brand: "", model: "", serial: "", accessories: "", problem_reported: "", technician_id: "" },
    ]);
  }

  function removeExtraDevice(i) {
    setExtraDevices((prev) => prev.filter((_, idx) => idx !== i));
    setExtraSubs((prev) => {
      const cp = { ...prev };
      delete cp[i];
      return cp;
    });
    setExtraModels((prev) => {
      const cp = { ...prev };
      delete cp[i];
      return cp;
    });
  }

  function updExtra(i, patch) {
    setExtraDevices((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }

  async function loadExtraSubs(i, categoryId) {
    if (!categoryId) {
      setExtraSubs((s) => ({ ...s, [i]: [] }));
      return;
    }
    try {
      const { data } = await api.get(`/subcategories?category_id=${categoryId}`);
      setExtraSubs((s) => ({ ...s, [i]: (data || []).filter((x) => x.active) }));
    } catch {
      setExtraSubs((s) => ({ ...s, [i]: [] }));
    }
  }

  // Auto-select default brand + load models filtered por subcategoría+marca para CADA equipo adicional
  // (mismas reglas automáticas que el equipo primario, aplicadas por índice).
  useEffect(() => {
    extraDevices.forEach((d, i) => {
      const subs = extraSubs[i] || [];
      const sub = subs.find((s) => s.id === d.subcategory_id);
      // 1) Auto-select default brand cuando la subcategoría lo tiene
      if (sub && sub.default_brand_id) {
        const b = brands.find((x) => x.id === sub.default_brand_id);
        if (b && d.brand !== b.name) {
          setExtraDevices((prev) => prev.map((dd, idx) => (idx === i ? { ...dd, brand: b.name } : dd)));
        }
      }
      // 2) Cargar modelos filtrados por subcategoría (+ marca si mapea al catálogo)
      if (!d.subcategory_id) {
        setExtraModels((m) => (m[i]?.length ? { ...m, [i]: [] } : m));
        return;
      }
      const brandObj = brands.find((b) => b.name.toLowerCase() === (d.brand || "").toLowerCase());
      const brand_id = brandObj?.id;
      const params = new URLSearchParams({ subcategory_id: d.subcategory_id });
      if (brand_id) params.append("brand_id", brand_id);
      api.get(`/models?${params.toString()}`)
        .then((r) => setExtraModels((m) => ({ ...m, [i]: (r.data || []).filter((x) => x.active !== false) })))
        .catch(() => setExtraModels((m) => ({ ...m, [i]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // reaccionamos a cambios en subcategoría o marca de cualquier equipo extra + a la lista de subcategorías cargada
    extraDevices.map((d) => `${d.subcategory_id}|${d.brand}`).join(","),
    Object.keys(extraSubs).map((k) => `${k}:${(extraSubs[k] || []).length}`).join(","),
    brands.length,
  ]);

  // Options for brand/model selects
  const brandOptions = useMemo(() => brands.map((b) => b.name), [brands]);
  const modelOptions = useMemo(() => models.map((m) => m.name), [models]);
  const brandDisabled = !form.subcategory_id;
  const modelDisabled = !form.subcategory_id || !form.brand;

  // Serial / IMEI is strictly required for Consolas and Teléfonos
  const SERIAL_REQUIRED_CATEGORY_NAMES = ["Consolas", "Teléfonos"];
  const selectedCategoryName = useMemo(
    () => categories.find((c) => c.id === form.category_id)?.name || "",
    [categories, form.category_id],
  );
  const serialRequired = SERIAL_REQUIRED_CATEGORY_NAMES.includes(selectedCategoryName);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">Nueva Orden</h1>
        <p className="text-slate-500 text-sm mt-1">Registra el ingreso de un equipo al taller.</p>
      </div>

      <form onSubmit={submit} className="card-base p-5 sm:p-6 space-y-5">
        {/* Customer */}
        <div>
          <Label>Cliente *</Label>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <CustomerSelect
                customers={customers}
                value={form.customer_id}
                onChange={(id) => upd("customer_id", id)}
                testid="new-order-customer"
              />
            </div>
            <button type="button" onClick={() => setShowNewCustomer(!showNewCustomer)} className="btn-secondary shrink-0" data-testid="new-order-toggle-new-customer">
              <UserPlus size={16} /> {showNewCustomer ? "Cerrar" : "Nuevo"}
            </button>
          </div>
          {showNewCustomer && (
            <div className="mt-3 p-3 bg-white/95 border border-slate-200 rounded-xl space-y-2">
              <input value={ncName} onChange={(e) => setNcName(e.target.value)} placeholder="Nombre *" className="input-base py-2" data-testid="inline-customer-name" />
              <input value={ncPhone} onChange={(e) => setNcPhone(e.target.value)} placeholder="Teléfono * (obligatorio)" className="input-base py-2" data-testid="inline-customer-phone" />
              <input value={ncEmail} onChange={(e) => setNcEmail(e.target.value)} placeholder="Email (opcional)" className="input-base py-2" data-testid="inline-customer-email" />
              <button type="button" onClick={createInlineCustomer} className="btn-primary w-full" data-testid="inline-customer-save">
                Guardar cliente
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Categoría *</Label>
            <select value={form.category_id} onChange={(e) => handleCategoryChange(e.target.value)} className="input-base" required data-testid="new-order-category">
              <option value="">—</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Subcategoría / Línea *</Label>
            <select value={form.subcategory_id} onChange={(e) => handleSubcategoryChange(e.target.value)} className="input-base" required disabled={!form.category_id} data-testid="new-order-subcategory">
              <option value="">—</option>
              {subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Marca</Label>
            <SearchableSelect
              options={brandOptions}
              value={form.brand}
              onChange={handleBrandChange}
              placeholder={brandDisabled ? "Elegí una subcategoría primero" : "Elegí marca…"}
              disabled={brandDisabled}
              testid="new-order-brand"
            />
          </div>
          <div>
            <Label>Modelo</Label>
            <SearchableSelect
              options={modelOptions}
              value={form.model}
              onChange={(v) => upd("model", v)}
              placeholder={
                modelDisabled
                  ? "Elegí una marca primero"
                  : modelOptions.length === 0
                    ? "Sin modelos precargados · usá manual"
                    : "Elegí modelo…"
              }
              disabled={modelDisabled}
              emptyLabel="Sin modelos precargados · usá 'Otro / Especificar manual'"
              testid="new-order-model"
            />
          </div>
          <div>
            <Label>{serialRequired ? "Serial / IMEI *" : "Serial / IMEI"}</Label>
            <input
              value={form.serial}
              onChange={(e) => upd("serial", e.target.value)}
              className={`input-base font-mono ${serialRequired && !form.serial.trim() ? "border-amber-400" : ""}`}
              placeholder={serialRequired ? "Obligatorio para esta categoría" : "Si aplica"}
              required={serialRequired}
              data-testid="new-order-serial"
            />
            {serialRequired && (
              <div className="text-[10px] text-amber-700 font-mono mt-1">
                Obligatorio para {selectedCategoryName.toLowerCase()} — registralo para garantía y trazabilidad.
              </div>
            )}
          </div>
          <div>
            <Label>Accesorios recibidos</Label>
            <input value={form.accessories} onChange={(e) => upd("accessories", e.target.value)} className="input-base" placeholder="Cargador, cable, control…" data-testid="new-order-accessories" />
          </div>
          <div>
            <Label>Sucursal de recepción *</Label>
            <select value={form.reception_branch_id} onChange={(e) => upd("reception_branch_id", e.target.value)} className="input-base" required data-testid="new-order-branch">
              <option value="">—</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          {user.role !== "technician" && (
            <div>
              <Label>Técnico asignado</Label>
              <select value={form.technician_id} onChange={(e) => upd("technician_id", e.target.value)} className="input-base" data-testid="new-order-technician">
                <option value="">Sin asignar</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div>
          <Label>Problema reportado *</Label>
          <textarea
            value={form.problem_reported}
            onChange={(e) => upd("problem_reported", e.target.value)}
            className="input-base min-h-[100px]"
            placeholder="Ej: No enciende. El cliente reporta caídas frecuentes."
            required
            data-testid="new-order-problem"
          />
        </div>
        <div>
          <Label>Observaciones</Label>
          <textarea
            value={form.observations}
            onChange={(e) => upd("observations", e.target.value)}
            className="input-base min-h-[80px]"
            placeholder="Estado físico, detalles adicionales…"
            data-testid="new-order-observations"
          />
        </div>

        {/* Reparación Express */}
        <div className={`rounded-2xl border-2 p-3 transition-colors ${form.is_express ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_express}
              onChange={(e) => upd("is_express", e.target.checked)}
              className="mt-1 w-5 h-5 accent-amber-600"
              data-testid="new-order-is-express"
            />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-slate-900 flex items-center gap-2">
                ⚡ Reparación Express
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Atención inmediata. Puede tener un recargo y aprobación al recibir el equipo.
              </div>
            </div>
          </label>

          {form.is_express && (
            <div className="mt-3 pl-8 space-y-3">
              <div>
                <Label>Recargo Express (opcional)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 font-mono text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.express_surcharge}
                    onChange={(e) => upd("express_surcharge", parseFloat(e.target.value) || 0)}
                    className="input-base"
                    placeholder="0.00"
                    data-testid="new-order-express-surcharge"
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Se suma al total del presupuesto visible al cliente.</div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.express_auto_approve}
                  onChange={(e) => upd("express_auto_approve", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-emerald-600"
                  data-testid="new-order-express-auto-approve"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">✓ Cliente autorizó al recibir el equipo</div>
                  <div className="text-[11px] text-slate-600">
                    El presupuesto quedará aprobado automáticamente. El técnico puede comenzar de inmediato.
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Multi-equipo: equipos adicionales dentro de la misma orden */}
        <div className="rounded-2xl border-2 border-dashed border-cyan-300 bg-cyan-50/40 p-3 sm:p-4" data-testid="new-order-extra-devices">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-heading font-bold text-slate-900 flex items-center gap-2">
                <Wrench size={18} /> Equipos adicionales
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Agrega más equipos del mismo cliente en esta misma orden (ej.: PS5 + 2 controles).
              </div>
            </div>
            <button type="button" onClick={addExtraDevice} className="btn-secondary shrink-0" data-testid="new-order-add-device">
              <Plus size={16} /> Agregar equipo
            </button>
          </div>

          {extraDevices.length === 0 && (
            <div className="mt-3 text-xs text-slate-500 italic">Ningún equipo adicional. La orden tendrá 1 solo equipo.</div>
          )}

          {extraDevices.map((d, i) => {
            const catName = categories.find((c) => c.id === d.category_id)?.name || "";
            const needsSerial = SERIAL_REQUIRED_CATEGORY_NAMES.includes(catName);
            return (
              <div key={i} className="mt-3 rounded-xl border border-cyan-300 bg-white p-3 space-y-3" data-testid={`extra-device-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-heading font-bold text-cyan-900">Equipo #{i + 2}</div>
                  <button type="button" onClick={() => removeExtraDevice(i)} className="text-slate-500 hover:text-rose-600 text-sm inline-flex items-center gap-1" data-testid={`extra-device-${i}-remove`}>
                    <XIcon size={14} /> Quitar
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Categoría *</Label>
                    <select
                      value={d.category_id}
                      onChange={(e) => {
                        updExtra(i, { category_id: e.target.value, subcategory_id: "", brand: "", model: "" });
                        loadExtraSubs(i, e.target.value);
                      }}
                      className="input-base"
                      required
                      data-testid={`extra-device-${i}-category`}
                    >
                      <option value="">—</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Subcategoría *</Label>
                    <select
                      value={d.subcategory_id}
                      onChange={(e) => updExtra(i, { subcategory_id: e.target.value, brand: "", model: "" })}
                      className="input-base"
                      required
                      disabled={!d.category_id}
                      data-testid={`extra-device-${i}-subcategory`}
                    >
                      <option value="">—</option>
                      {(extraSubs[i] || []).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Marca</Label>
                    <SearchableSelect
                      options={brandOptions}
                      value={d.brand}
                      onChange={(v) => updExtra(i, { brand: v, model: v !== d.brand ? "" : d.model })}
                      placeholder="Seleccionar marca…"
                      testid={`extra-device-${i}-brand`}
                    />
                  </div>
                  <div>
                    <Label>Modelo</Label>
                    <SearchableSelect
                      options={(extraModels[i] || []).map((m) => m.name)}
                      value={d.model}
                      onChange={(v) => updExtra(i, { model: v })}
                      placeholder={d.subcategory_id ? "Seleccionar modelo…" : "Elegí subcategoría primero"}
                      disabled={!d.subcategory_id}
                      testid={`extra-device-${i}-model`}
                      emptyLabel="Sin modelos catalogados"
                    />
                  </div>
                  <div>
                    <Label>{needsSerial ? "Serial / IMEI *" : "Serial / IMEI"}</Label>
                    <input value={d.serial} onChange={(e) => updExtra(i, { serial: e.target.value })} className={`input-base font-mono ${needsSerial && !d.serial ? "border-amber-400" : ""}`} placeholder={needsSerial ? "Obligatorio" : "Si aplica"} required={needsSerial} data-testid={`extra-device-${i}-serial`} />
                  </div>
                  <div>
                    <Label>Accesorios</Label>
                    <input value={d.accessories} onChange={(e) => updExtra(i, { accessories: e.target.value })} className="input-base" placeholder="Cargador, cable…" data-testid={`extra-device-${i}-accessories`} />
                  </div>
                  {user.role !== "technician" && (
                    <div className="sm:col-span-2">
                      <Label>Técnico asignado</Label>
                      <select value={d.technician_id} onChange={(e) => updExtra(i, { technician_id: e.target.value })} className="input-base" data-testid={`extra-device-${i}-technician`}>
                        <option value="">Sin asignar</option>
                        {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Problema reportado *</Label>
                  <textarea
                    value={d.problem_reported}
                    onChange={(e) => updExtra(i, { problem_reported: e.target.value })}
                    className="input-base min-h-[70px]"
                    placeholder="Describe el problema de este equipo…"
                    required
                    data-testid={`extra-device-${i}-problem`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <button type="submit" disabled={saving} className="btn-primary w-full text-base" data-testid="new-order-submit">
          <Save size={18} /> {saving ? "Guardando…" : `Registrar orden${extraDevices.length ? ` (${extraDevices.length + 1} equipos)` : ""}`}
        </button>
      </form>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="label-strong">{children}</div>
  );
}
